import { SettingKey } from 'data/ppoints/constants';
import { ExtensionInfo, useEmbeddedStatusSwr } from 'hooks/embedded-status/useEmbeddedStatusSwr';
import useSettings from 'hooks/useSettings';
import { IntegrationModeSetting } from 'models/ppoints/settings/SettingsToEcom';
import { useMemo } from 'react';
import {
    findCheckoutMapExtension,
    findThankYouPageExtension,
    findThankYouPageMapExtension,
    findThemeAppExtension,
    hasAppsEmbedBlock,
    hasBlockEmbed,
    hasCheckoutMap,
    hasCustomerAccountPage,
    hasOrderStatusPageMap,
    hasThankYouPage,
    hasThankYouPageMap,
} from './integrationStatusUtils';

export const getShownIntegrationStatuses = (
    extensions: ExtensionInfo[],
    integrationMode: IntegrationModeSetting,
    hideOptional?: boolean
): boolean[] => {
    const statuses: boolean[] = [];

    if (integrationMode === IntegrationModeSetting.map) {
        const themeAppExtension = findThemeAppExtension(extensions);
        statuses.push(hasAppsEmbedBlock(themeAppExtension));

        if (!hideOptional) {
            statuses.push(hasBlockEmbed(themeAppExtension));
        }
    }

    if (integrationMode === IntegrationModeSetting.checkout_map) {
        statuses.push(hasCheckoutMap(findCheckoutMapExtension(extensions)));
    }

    if (integrationMode === IntegrationModeSetting.thank_you_page_map) {
        const thankYouPageMapIntegration = findThankYouPageMapExtension(extensions);
        statuses.push(
            hasThankYouPageMap(thankYouPageMapIntegration),
            hasOrderStatusPageMap(thankYouPageMapIntegration)
        );
    }

    if (!hideOptional) {
        const thankYouPageIntegration = findThankYouPageExtension(extensions);
        statuses.push(hasThankYouPage(thankYouPageIntegration), hasCustomerAccountPage(thankYouPageIntegration));
    }

    return statuses;
};

export const useIntegrationReadiness = ({ hideOptional }: UseIntegrationReadinessOptions = {}) => {
    const { data, isLoading: isLoadingExtensions } = useEmbeddedStatusSwr();
    const setting = useSettings<IntegrationModeSetting>(SettingKey.integration_mode);

    const integrationMode = useMemo<IntegrationModeSetting>(() => setting.settings, [setting.settings]);

    const isLoading = useMemo(() => isLoadingExtensions || setting.fetching, [isLoadingExtensions, setting.fetching]);

    const isReady = useMemo(() => {
        if (!integrationMode || !data) {
            return false;
        }

        const statuses = getShownIntegrationStatuses(data, integrationMode, hideOptional);

        return statuses.length > 0 && statuses.every(Boolean);
    }, [data, integrationMode, hideOptional]);

    return { isReady, isLoading };
};

interface UseIntegrationReadinessOptions {
    hideOptional?: boolean;
}
